import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, MagicWand, Warning, CheckCircle, CameraPlus, Clock, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Modal admin para diagnóstico + recuperação de overrides item-a-item
 * (color_breakdown, silk_color, image_url, production_notes) perdidos
 * em resyncs anteriores do Bling. Usa o `history` de cada pedido.
 */
export default function RecoverOverridesModal({ onClose }) {
  const [tab, setTab] = useState("history"); // "history" | "snapshots"
  const [loading, setLoading] = useState(true);
  const [dryRun, setDryRun] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [executed, setExecuted] = useState(null);
  const [error, setError] = useState("");

  // Snapshots
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [snapshotDryRun, setSnapshotDryRun] = useState(null);
  const [restoringSnapshot, setRestoringSnapshot] = useState(false);

  const loadSnapshots = async () => {
    setSnapshotsLoading(true);
    try {
      const { data } = await api.get("/orders/maintenance/customizations-snapshots");
      setSnapshots(data.snapshots || []);
    } catch (e) {
      toast.error("Falha ao carregar snapshots");
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const takeSnapshot = async () => {
    setTakingSnapshot(true);
    try {
      const { data } = await api.post("/orders/maintenance/customizations-snapshots/take");
      toast.success(`Snapshot criado (${data.orders_count} pedidos)`);
      await loadSnapshots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar snapshot");
    } finally {
      setTakingSnapshot(false);
    }
  };

  const previewSnapshotRestore = async (snapshotId) => {
    setSelectedSnapshot(snapshotId);
    setSnapshotDryRun(null);
    try {
      const { data } = await api.post(
        `/orders/maintenance/customizations-snapshots/${snapshotId}/restore?dry_run=true`
      );
      setSnapshotDryRun(data);
    } catch (e) {
      toast.error("Falha no dry-run do snapshot");
    }
  };

  const executeSnapshotRestore = async () => {
    if (!snapshotDryRun || !selectedSnapshot) return;
    if (!confirm(
      `Isto vai restaurar ${snapshotDryRun.restored_orders} pedido(s) usando o snapshot de ${new Date(snapshotDryRun.snapshot_taken_at).toLocaleString("pt-BR")}.\n\nContinuar?`
    )) return;
    setRestoringSnapshot(true);
    try {
      const { data } = await api.post(
        `/orders/maintenance/customizations-snapshots/${selectedSnapshot}/restore?dry_run=false`
      );
      toast.success(`${data.restored_orders} pedido(s) restaurado(s) do snapshot`);
      setSnapshotDryRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na restauração");
    } finally {
      setRestoringSnapshot(false);
    }
  };

  const runDryRun = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/orders/maintenance/recover-item-overrides?dry_run=true");
      setDryRun(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao diagnosticar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runDryRun(); /* eslint-disable-next-line */ }, []);

  const executeRecovery = async () => {
    if (!confirm(
      `Isto vai restaurar as cores/silk/imagens de ${dryRun?.restored_orders} pedido(s) baseado no histórico deles.\n\nCada pedido receberá uma entrada 'items_customizations_recovered' no history.\n\nContinuar?`
    )) return;
    setExecuting(true);
    setError("");
    try {
      const { data } = await api.post("/orders/maintenance/recover-item-overrides?dry_run=false");
      setExecuted(data);
      toast.success(`${data.restored_orders} pedido(s) restaurado(s)`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao executar recuperação");
    } finally {
      setExecuting(false);
    }
  };

  const preview = executed || dryRun;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="glass max-w-3xl w-full p-6 md:p-8 my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="recover-overrides-modal"
      >
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <div className="overline text-zinc-500 mb-1">MANUTENÇÃO</div>
            <h2 className="font-head text-2xl font-black flex items-center gap-2">
              <MagicWand size={26} weight="duotone" /> Recuperar Cores & Overrides
            </h2>
            <p className="text-sm text-zinc-600 mt-2 leading-snug">
              Restaura <strong>cores dos copos</strong>, <strong>cor do silk</strong>,{" "}
              <strong>imagem por item</strong> e <strong>data limite de envio</strong> a
              partir do histórico do pedido, para os casos em que um sync do Bling
              anterior apagou esses dados.
            </p>
          </div>
          <button onClick={onClose} aria-label="Fechar" className="shrink-0">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-300 mb-4 -mx-6 md:-mx-8 px-6 md:px-8">
          <button
            className={`px-4 py-2 text-sm font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              tab === "history"
                ? "border-black text-black"
                : "border-transparent text-zinc-400 hover:text-zinc-700"
            }`}
            onClick={() => setTab("history")}
            data-testid="tab-history"
          >
            <MagicWand size={14} className="inline mr-1" /> Do Histórico
          </button>
          <button
            className={`px-4 py-2 text-sm font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              tab === "snapshots"
                ? "border-black text-black"
                : "border-transparent text-zinc-400 hover:text-zinc-700"
            }`}
            onClick={() => setTab("snapshots")}
            data-testid="tab-snapshots"
          >
            <Clock size={14} className="inline mr-1" /> Snapshots Diários
          </button>
        </div>

      {tab === "history" && (<>
        {loading && (
          <div className="text-center py-12 text-zinc-400">Analisando pedidos…</div>
        )}

        {error && (
          <div className="border-2 border-[#E60000] bg-red-50 text-[#E60000] p-3 mb-4 text-sm">
            <Warning size={16} weight="bold" className="inline mr-1" /> {error}
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Pedidos varridos" value={preview.scanned} />
              <Stat label="Com dados faltando" value={preview.with_missing_overrides} />
              <Stat label="Recuperáveis" value={preview.with_recoverable_history} accent="green" />
              <Stat
                label={executed ? "Restaurados" : "A restaurar"}
                value={preview.restored_orders}
                accent="blue"
              />
            </div>

            <div className="border border-zinc-200 bg-white p-4">
              <div className="overline text-zinc-500 mb-2">CAMPOS QUE SERIAM RESTAURADOS</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <FieldRow label="Cores dos copos" v={preview.restored_fields.color_breakdown} />
                <FieldRow label="Cor do silk" v={preview.restored_fields.silk_color} />
                <FieldRow label="Imagens" v={preview.restored_fields.image_url} />
                <FieldRow label="Notas de produção" v={preview.restored_fields.production_notes} />
                <FieldRow label="Data limite envio" v={preview.restored_fields.exit_date} />
              </div>
            </div>

            {preview.by_status && (
              <div className="border border-zinc-200 bg-white p-4">
                <div className="overline text-zinc-500 mb-2">POR STATUS</div>
                <div className="flex gap-4 flex-wrap text-sm">
                  {Object.entries(preview.by_status).map(([k, v]) => (
                    <div key={k} className="font-mono">
                      <span className="text-zinc-500">{k}:</span>{" "}
                      <span className="font-bold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.samples && preview.samples.length > 0 && (
              <div className="border border-zinc-200 bg-white p-4">
                <div className="overline text-zinc-500 mb-2">
                  AMOSTRA (PRIMEIROS {preview.samples.length})
                </div>
                <div className="space-y-1 text-xs font-mono">
                  {preview.samples.map((s, i) => (
                    <div key={`${s.bling_number}-${i}`} className="flex flex-wrap gap-x-3">
                      <span className="font-bold">#{s.bling_number}</span>
                      <span className="text-zinc-500">{s.status}</span>
                      <span>
                        {s.restored.color_breakdown ? `${s.restored.color_breakdown} cor` : ""}
                        {s.restored.silk_color ? ` +${s.restored.silk_color} silk` : ""}
                        {s.restored.image_url ? ` +${s.restored.image_url} img` : ""}
                        {s.restored.exit_date ? ` +envio ${s.exit_date || ""}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {executed ? (
              <div className="border-2 border-green-600 bg-green-50 text-green-800 p-4 flex items-center gap-2">
                <CheckCircle size={20} weight="fill" />
                <div>
                  <div className="font-bold">Recuperação concluída</div>
                  <div className="text-sm">
                    {executed.restored_orders} pedido(s) atualizados. Cada um recebeu uma
                    entrada <code>items_customizations_recovered</code> no history.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  className="btn btn-ghost"
                  onClick={onClose}
                  data-testid="recover-cancel-btn"
                >
                  Fechar
                </button>
                <button
                  className="btn btn-klein"
                  onClick={executeRecovery}
                  disabled={executing || preview.restored_orders === 0}
                  data-testid="recover-execute-btn"
                >
                  <MagicWand size={16} weight="bold" />
                  {restoreLabel(executing, preview.restored_orders)}
                </button>
              </div>
            )}
          </div>
        )}
      </>)}

      {tab === "snapshots" && (
        <div className="space-y-4">
          <div className="border border-zinc-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="overline text-zinc-500 mb-1">PROTEÇÃO EXTRA</div>
                <div className="text-sm text-zinc-700 leading-snug">
                  Snapshots diários automáticos salvam <strong>só as customizações</strong>
                  {" "}(cor, silk, imagem, data envio) por 30 dias. Se algo se perder, você
                  restaura em segundos sem tocar no banco inteiro.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-klein btn-tight"
                onClick={takeSnapshot}
                disabled={takingSnapshot}
                data-testid="snapshot-take-now"
              >
                <CameraPlus size={14} weight="bold" />
                {takingSnapshot ? "Salvando…" : "Tirar agora"}
              </button>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white">
            <div className="overline text-zinc-500 px-4 py-2 border-b border-zinc-200">
              SNAPSHOTS DISPONÍVEIS ({snapshots.length})
            </div>
            {snapshotsLoading && (
              <div className="p-6 text-center text-zinc-400">Carregando…</div>
            )}
            {!snapshotsLoading && snapshots.length === 0 && (
              <div className="p-6 text-center text-zinc-500 text-sm">
                Nenhum snapshot ainda. Clique em "Tirar agora" para criar o primeiro.
              </div>
            )}
            {!snapshotsLoading && snapshots.length > 0 && (
              <div className="max-h-64 overflow-y-auto divide-y divide-zinc-100">
                {snapshots.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => previewSnapshotRestore(s.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-yellow-50 flex items-center justify-between gap-3 ${
                      selectedSnapshot === s.id ? "bg-yellow-50" : ""
                    }`}
                    data-testid={`snapshot-item-${s.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-sm">
                        {new Date(s.taken_at).toLocaleString("pt-BR")}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {s.orders_count} pedido(s) capturados
                      </div>
                    </div>
                    <ArrowCounterClockwise size={16} weight="bold" className="text-zinc-400" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {snapshotDryRun && (
            <div className="border-2 border-black bg-white p-4">
              <div className="overline text-zinc-500 mb-2">
                {snapshotDryRun.dry_run ? "PRÉVIA DA RESTAURAÇÃO" : "RESULTADO"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                <Stat label="Encontrados" value={snapshotDryRun.matched_orders} />
                <Stat label="A restaurar" value={snapshotDryRun.restored_orders} accent="blue" />
                <Stat label="Do snapshot" value={snapshotDryRun.considered} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs mb-3">
                <FieldRow label="Cores" v={snapshotDryRun.restored_fields.color_breakdown} />
                <FieldRow label="Silk" v={snapshotDryRun.restored_fields.silk_color} />
                <FieldRow label="Imagens" v={snapshotDryRun.restored_fields.image_url} />
                <FieldRow label="Notas" v={snapshotDryRun.restored_fields.production_notes} />
                <FieldRow label="Data envio" v={snapshotDryRun.restored_fields.exit_date} />
              </div>
              {snapshotDryRun.dry_run ? (
                <div className="flex items-center justify-end">
                  <button
                    className="btn btn-klein"
                    onClick={executeSnapshotRestore}
                    disabled={restoringSnapshot || snapshotDryRun.restored_orders === 0}
                    data-testid="snapshot-restore-btn"
                  >
                    <ArrowCounterClockwise size={14} weight="bold" />
                    {restoreLabel(restoringSnapshot, snapshotDryRun.restored_orders)}
                  </button>
                </div>
              ) : (
                <div className="border-2 border-green-600 bg-green-50 text-green-800 p-3 text-sm">
                  <CheckCircle size={16} weight="fill" className="inline mr-1" />
                  {snapshotDryRun.restored_orders} pedido(s) restaurados
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

const STAT_COLORS = { green: "text-green-700", blue: "text-[#002FA7]" };

function restoreLabel(running, count) {
  if (running) return "Restaurando…";
  if (count === 0) return "Nada a restaurar";
  return `Restaurar ${count} pedido(s)`;
}

function Stat({ label, value, accent }) {
  const color = STAT_COLORS[accent] || "text-black";
  return (
    <div className="border border-zinc-200 bg-white p-3">
      <div className="overline text-[10px] text-zinc-500">{label}</div>
      <div className={`font-mono font-black text-2xl leading-none mt-1 ${color}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

function FieldRow({ label, v }) {
  return (
    <div className="flex justify-between gap-2 border-b border-zinc-100 pb-1">
      <span className="text-zinc-500">{label}</span>
      <span className="font-mono font-bold">{v || 0}</span>
    </div>
  );
}
